import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  checkboxClass,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getRnEvalNoteForm,
  updateRnEvalNoteForm,
} from "../../../../../services/patientService";
import {
  BrainIcon,
  CheckCircleIcon,
  FileCheckIcon,
  GraduationCapIcon,
  HeartPulseIcon,
  HelpingIcon,
  HomeIcon,
  MedicalNotesIcon,
  NurseIcon,
  RunningIcon,
  ShieldCheckIcon,
  TargetIcon,
  UtensilsIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { PATIENT_THEME } from "../../../patientTheme";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  RN_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  AMBULATORY_STATUS_OPTIONS,
  APPETITE_OPTIONS,
  ASSESSMENT_UNDERSTANDING_OPTIONS,
  ASSISTIVE_DEVICES_OPTIONS,
  CARDIOVASCULAR_OPTIONS,
  DIETARY_RESTRICTIONS_OPTIONS,
  EDUCATION_TOPICS_OPTIONS,
  GASTROINTESTINAL_OPTIONS,
  GENERAL_SYSTEMS_OPTIONS,
  HOME_SAFETY_OPTIONS,
  ADVANCE_DIRECTIVES,
  INTEGUMENTARY_OPTIONS,
  LIVES_WITH_OPTIONS,
  NEURO_OPTIONS,
  NURSING_DIAGNOSES_OPTIONS,
  NURSING_INTERVENTIONS_OPTIONS,
  ORIENTATION_OPTIONS,
  PRESCRIBED_DIET_TYPE_OPTIONS,
  RESIDENCE_TYPE_OPTIONS,
  RESPIRATORY_OPTIONS,
  RISK_IDENTIFIERS_OPTIONS,
  SAFETY_AWARENESS_OPTIONS,
  SKIN_INTEGRITY_OPTIONS,
  TRANSFER_ABILITY_OPTIONS,
  emptyRnEvalPayload,
  toggleInList,
} from "./rnEvalNoteContent";
import NurseNotesCommonHeader from "./NurseNotesCommonHeader";
import ClinicalVitalsFields from "./ClinicalVitalsFields";
import WoundPresentSection from "./WoundPresentSection";
import { applyWoundAssessmentPayload } from "./woundAssessmentContent";
import { normalizeClinicalVitals } from "./clinicalVitals";
import {
  normalizeCommonHeaderState,
  resolveNurseNotesCommonHeader,
  withNormalizedCommonHeader,
} from "./nurseNotesCommonHeaderUtils";
import {
  assertNurseVisitMetaForFlag,
  buildNurseVisitMetaProps,
  pickNurseVisitMeta,
} from "./nurseVisitCommon";

const EVAL_DATE_MAX = new Date().toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

const emptyValues = () => ({
  ...emptyRnEvalPayload(),
  evalDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const RnEvalNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyRnEvalPayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickNurseVisitMeta(loadedPayload, data),
        ...normalizeClinicalVitals(loadedPayload),
        evalDate: data.evalDate || loadedPayload.evalDate || data.visitDate || today(),
        commonHeader: normalizeCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        neurological: Array.isArray(loadedPayload.neurological)
          ? loadedPayload.neurological
          : [],
        cardiovascular: Array.isArray(loadedPayload.cardiovascular)
          ? loadedPayload.cardiovascular
          : [],
        respiratory: Array.isArray(loadedPayload.respiratory)
          ? loadedPayload.respiratory
          : [],
        integumentary: Array.isArray(loadedPayload.integumentary)
          ? loadedPayload.integumentary
          : [],
        gastrointestinal: Array.isArray(loadedPayload.gastrointestinal)
          ? loadedPayload.gastrointestinal
          : [],
        skinIntegrity: Array.isArray(loadedPayload.skinIntegrity)
          ? loadedPayload.skinIntegrity
          : [],
        general: Array.isArray(loadedPayload.general) ? loadedPayload.general : [],
        dietaryRestrictions: Array.isArray(loadedPayload.dietaryRestrictions)
          ? loadedPayload.dietaryRestrictions
          : [],
        orientation: Array.isArray(loadedPayload.orientation)
          ? loadedPayload.orientation
          : [],
        transferAbility: Array.isArray(loadedPayload.transferAbility)
          ? loadedPayload.transferAbility
          : [],
        assistiveDevices: Array.isArray(loadedPayload.assistiveDevices)
          ? loadedPayload.assistiveDevices
          : [],
        nursingDiagnoses: Array.isArray(loadedPayload.nursingDiagnoses)
          ? loadedPayload.nursingDiagnoses
          : [],
        educationTopicsCovered: Array.isArray(loadedPayload.educationTopicsCovered)
          ? loadedPayload.educationTopicsCovered
          : [],
        riskIdentifiers: Array.isArray(loadedPayload.riskIdentifiers)
          ? loadedPayload.riskIdentifiers
          : [],
        nursingInterventions: Array.isArray(loadedPayload.nursingInterventions)
          ? loadedPayload.nursingInterventions
          : [],
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      applyWoundAssessmentPayload(merged, merged, {
        mrn: patient?.mrn,
      });

      setValues(merged);
      resetSignatures();
    },
    [patient?.mrn, patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveNurseNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Nurse Evaluation Note",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            evalDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...emptyRnEvalPayload(),
              commonHeader,
            },
          });
          return;
        }

        const res = await getRnEvalNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveNurseNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            commonHeader,
          },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  /**
   * Only RN/LVN may fill this note; Admin/OA retain their review powers.
   * Every other role — including other clinical disciplines — is view-only.
   */
  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        RN_FORM_EDITOR_ROLES,
        "View only — only RN/LVN can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      evalDate,
      ...rnFields
    } = values;

    const nestedPayload = withNormalizedCommonHeader({
      ...rnFields,
      onsetDate: values.onsetDate ? formatDateForInput(values.onsetDate) : "",
      surgicalDate: values.surgicalDate
        ? formatDateForInput(values.surgicalDate)
        : "",
      estimatedDischargeDate: values.estimatedDischargeDate
        ? formatDateForInput(values.estimatedDischargeDate)
        : "",
    });

    const payload = {
      flag,
      evalDate: evalDate ? formatDateForInput(evalDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    const visitMetaError = assertNurseVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateRnEvalNoteForm,
        filledByUserId,
        scheduleDate: values.evalDate
          ? formatDateForInput(values.evalDate)
          : null,
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Nurse Evaluation
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Documentation for skilled nursing home health services.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn || values.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              values.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Date"
          dateValue={displayDateUS(values.evalDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Date
                </div>
                <InputDatePicker
                  name="evalDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.evalDate) || ""}
                  onChange={(event) => setValue("evalDate", event.target.value)}
                  max={EVAL_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildNurseVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <NurseNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <Section icon={MedicalNotesIcon} title="7. Medical History">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Onset Date">
              <DateFieldInput
                name="onsetDate"
                value={values.onsetDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("onsetDate", v)}
              />
            </Field>
            <Field label="Surgical Date">
              <DateFieldInput
                name="surgicalDate"
                value={values.surgicalDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("surgicalDate", v)}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Pertinent Clinical History">
                <textarea
                  rows={4}
                  className={textareaClass}
                  value={values.pertinentClinicalHistory}
                  disabled={!canEdit}
                  placeholder="Relevant medical/surgical history, hospitalizations, prior functional status…"
                  onChange={(e) =>
                    setValue("pertinentClinicalHistory", e.target.value)
                  }
                />
              </Field>
            </div>
          </div>
        </Section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Section icon={HomeIcon} title="8. Living Situation">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2 mb-3">
              <Field label="Residence Type">
                <select
                  className={fieldClass}
                  value={values.residenceType}
                  disabled={!canEdit}
                  onChange={(e) => setValue("residenceType", e.target.value)}
                >
                  <option value="">Select…</option>
                  {RESIDENCE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Lives With">
                <select
                  className={fieldClass}
                  value={values.livesWith}
                  disabled={!canEdit}
                  onChange={(e) => setValue("livesWith", e.target.value)}
                >
                  <option value="">Select…</option>
                  {LIVES_WITH_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Home Safety Assessment
            </div>
            <div className="grid grid-cols-3 gap-2">
              {HOME_SAFETY_OPTIONS.map((opt) => {
                const active = values.homeSafety === opt;
                const isUnsafe = opt === "Unsafe";
                return (
                  <button
                    key={opt}
                    type="button"
                    disabled={!canEdit}
                    onClick={() => setValue("homeSafety", opt)}
                    className={`min-h-10 px-2 py-2 rounded-md text-[11px] font-semibold leading-snug border transition-colors disabled:opacity-60 whitespace-normal text-center ${
                      active
                        ? isUnsafe
                          ? "bg-rose-600 text-white border-rose-600"
                          : "bg-indigo-600 text-white border-indigo-600"
                        : isUnsafe
                          ? "bg-white text-rose-600 border-rose-200 hover:bg-rose-50"
                          : "bg-indigo-50/60 text-indigo-800 border-indigo-100 hover:bg-indigo-100"
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </Section>

          <Section icon={ShieldCheckIcon} title="9. Advance Directives">
            <div className="space-y-1.5">
              {ADVANCE_DIRECTIVES.map((item) => (
                <label
                  key={item.key}
                  className={`flex items-start gap-2.5 rounded-md border px-3 py-2 transition-colors ${
                    values[item.key]
                      ? "border-indigo-300 bg-indigo-50/60"
                      : "border-slate-200 hover:border-slate-300"
                  } ${canEdit ? "cursor-pointer" : "cursor-default"}`}
                >
                  <input
                    type="checkbox"
                    className={checkboxClass}
                    checked={Boolean(values[item.key])}
                    disabled={!canEdit}
                    onChange={(e) => setValue(item.key, e.target.checked)}
                  />
                  <span className="min-w-0">
                    <span className="block text-[10px] font-bold text-slate-800">
                      {item.title}
                    </span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">
                      {item.description}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </Section>
        </div>

        <Section icon={HeartPulseIcon} title="10. Clinical Vitals & Trends">
          <ClinicalVitalsFields
            values={values}
            canEdit={canEdit}
            onChange={(key, next) => setValue(key, next)}
          />
        </Section>

        <Section icon={NurseIcon} title="Wound Assessment (If Applicable)">
          <WoundPresentSection
            values={values}
            canEdit={canEdit}
            printMode={printMode}
            onChange={(key, next) => setValue(key, next)}
            onToggleList={(key, opt) => toggleListValue(key, opt)}
          />
        </Section>

        <Section icon={NurseIcon} title="11. Systems Review (Review of Systems)">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              ["Neurological", "neurological", NEURO_OPTIONS],
              ["Cardiovascular", "cardiovascular", CARDIOVASCULAR_OPTIONS],
              ["Respiratory", "respiratory", RESPIRATORY_OPTIONS],
              ["Integumentary", "integumentary", INTEGUMENTARY_OPTIONS],
              ["Gastrointestinal", "gastrointestinal", GASTROINTESTINAL_OPTIONS],
              ["Skin Integrity", "skinIntegrity", SKIN_INTEGRITY_OPTIONS],
              ["General", "general", GENERAL_SYSTEMS_OPTIONS],
            ].map(([label, key, options]) => (
              <div
                key={key}
                className="rounded-md border border-slate-200 bg-slate-50/40 p-2.5"
              >
                <div
                  className={`text-[9px] font-semibold uppercase tracking-wide mb-1.5 ${PATIENT_THEME.section.title}`}
                >
                  {label}
                </div>
                <div className="space-y-1">
                  {options.map((opt) => (
                    <label
                      key={opt}
                      className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className={checkboxClass}
                        checked={values[key].includes(opt)}
                        disabled={!canEdit}
                        onChange={() => toggleListValue(key, opt)}
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <Field label="Additional Observations">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.additionalObservations}
                disabled={!canEdit}
                placeholder="Enter other significant findings..."
                onChange={(e) =>
                  setValue("additionalObservations", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Section icon={UtensilsIcon} title="12. Nutrition & Hydration">
            <div className="mb-3">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Appetite
              </div>
              <div className="flex flex-wrap gap-1.5">
                {APPETITE_OPTIONS.map((opt) => {
                  const active = values.appetite === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => setValue("appetite", opt)}
                      className={`h-7 px-2.5 rounded text-[11px] font-medium border transition-colors disabled:opacity-60 ${
                        active
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-indigo-50/60 text-indigo-800 border-indigo-100 hover:bg-indigo-100"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mb-3">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Dietary Restrictions
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {DIETARY_RESTRICTIONS_OPTIONS.map((opt) => (
                  <label
                    key={opt}
                    className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className={checkboxClass}
                      checked={values.dietaryRestrictions.includes(opt)}
                      disabled={!canEdit}
                      onChange={() => toggleListValue("dietaryRestrictions", opt)}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
            <Field label="Prescribed Diet Type">
              <select
                className={fieldClass}
                value={values.prescribedDietType}
                disabled={!canEdit}
                onChange={(e) => setValue("prescribedDietType", e.target.value)}
              >
                <option value="">Select…</option>
                {PRESCRIBED_DIET_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Field>
          </Section>

          <Section icon={BrainIcon} title="13. Mental & Psychosocial">
            <div className="mb-3">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Orientation
              </div>
              <div className="flex flex-wrap gap-1.5">
                {ORIENTATION_OPTIONS.map((opt) => {
                  const active = values.orientation.includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => toggleListValue("orientation", opt)}
                      className={`h-7 px-2.5 rounded-full text-[11px] font-medium border transition-colors disabled:opacity-60 ${
                        active
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-indigo-50/60 text-indigo-800 border-indigo-100 hover:bg-indigo-100"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
            <Field label="Safety Awareness">
              <select
                className={fieldClass}
                value={values.safetyAwareness}
                disabled={!canEdit}
                onChange={(e) => setValue("safetyAwareness", e.target.value)}
              >
                <option value="">Select…</option>
                {SAFETY_AWARENESS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Field>
          </Section>
        </div>

        <Section icon={RunningIcon} title="14. Functional Assessment">
          <Field label="Ambulatory Status">
            <select
              className={`${fieldClass} max-w-sm`}
              value={values.ambulatoryStatus}
              disabled={!canEdit}
              onChange={(e) => setValue("ambulatoryStatus", e.target.value)}
            >
              <option value="">Select…</option>
              {AMBULATORY_STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </Field>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Transfer Ability
              </div>
              <div className="grid grid-cols-1 gap-1">
                {TRANSFER_ABILITY_OPTIONS.map((opt) => (
                  <label
                    key={opt}
                    className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className={checkboxClass}
                      checked={values.transferAbility.includes(opt)}
                      disabled={!canEdit}
                      onChange={() => toggleListValue("transferAbility", opt)}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Assistive Devices
              </div>
              <div className="grid grid-cols-2 gap-1">
                {ASSISTIVE_DEVICES_OPTIONS.map((opt) => (
                  <label
                    key={opt}
                    className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className={checkboxClass}
                      checked={values.assistiveDevices.includes(opt)}
                      disabled={!canEdit}
                      onChange={() => toggleListValue("assistiveDevices", opt)}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </Section>

        <Section icon={MedicalNotesIcon} title="15. Nursing Diagnoses / Problems Identified">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Common Diagnoses
              </div>
              <div className="space-y-1.5">
                {NURSING_DIAGNOSES_OPTIONS.map((opt) => (
                  <label
                    key={opt}
                    className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer rounded-md border border-slate-200 bg-white px-2.5 py-2"
                  >
                    <input
                      type="checkbox"
                      className={checkboxClass}
                      checked={values.nursingDiagnoses.includes(opt)}
                      disabled={!canEdit}
                      onChange={() => toggleListValue("nursingDiagnoses", opt)}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
            <Field label="Narrative Diagnosis & Clinical Rationale">
              <textarea
                rows={8}
                className={textareaClass}
                value={values.nursingDiagnosesNarrative}
                disabled={!canEdit}
                placeholder="Detail the identified nursing problems, supporting clinical evidence, and prioritize based on patient acuity..."
                onChange={(e) =>
                  setValue("nursingDiagnosesNarrative", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        <Section icon={GraduationCapIcon} title="16. Patient / Caregiver Education">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Topics Covered This Visit
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {EDUCATION_TOPICS_OPTIONS.map((opt) => (
                  <label
                    key={opt}
                    className={`flex items-center gap-2 text-[12px] text-slate-700 rounded-md border px-2.5 py-2 ${
                      canEdit ? "cursor-pointer" : "cursor-default"
                    } ${
                      values.educationTopicsCovered.includes(opt)
                        ? "border-indigo-300 bg-indigo-50/60"
                        : "border-slate-200 bg-white"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className={checkboxClass}
                      checked={values.educationTopicsCovered.includes(opt)}
                      disabled={!canEdit}
                      onChange={() =>
                        toggleListValue("educationTopicsCovered", opt)
                      }
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Assessment of Understanding
              </div>
              <div className="space-y-1.5">
                {ASSESSMENT_UNDERSTANDING_OPTIONS.map((opt) => {
                  const active = values.assessmentOfUnderstanding === opt;
                  return (
                    <label
                      key={opt}
                      className={`flex items-center justify-between gap-2 text-[12px] text-slate-700 rounded-md border px-2.5 py-2 ${
                        canEdit ? "cursor-pointer" : "cursor-default"
                      } ${
                        active
                          ? "border-indigo-300 bg-indigo-50/60"
                          : "border-slate-200 bg-white"
                      }`}
                    >
                      <span>{opt}</span>
                      <input
                        type="radio"
                        name="assessmentOfUnderstanding"
                        className={checkboxClass}
                        checked={active}
                        disabled={!canEdit}
                        onChange={() =>
                          setValue("assessmentOfUnderstanding", opt)
                        }
                      />
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </Section>

        <Section icon={FileCheckIcon} title="17. Nursing Summary & Impression">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2">
              <Field label="Professional Clinical Assessment Impressions">
                <textarea
                  rows={7}
                  className={textareaClass}
                  value={values.nursingImpressions}
                  disabled={!canEdit}
                  placeholder="Describe clinical findings, response to treatment, and overall status..."
                  onChange={(e) => setValue("nursingImpressions", e.target.value)}
                />
              </Field>
            </div>
            <div className="rounded-md border border-rose-200 bg-rose-50/40 p-3">
              <div className="text-[9px] font-bold uppercase tracking-wide text-rose-700 mb-2">
                Risk Identifiers
              </div>
              <div className="space-y-1.5">
                {RISK_IDENTIFIERS_OPTIONS.map((opt) => {
                  const active = values.riskIdentifiers.includes(opt);
                  return (
                    <label
                      key={opt}
                      className={`flex items-center gap-2 text-[12px] cursor-pointer rounded-md border px-2 py-1.5 ${
                        active
                          ? "border-rose-300 bg-white text-rose-800"
                          : "border-rose-100 bg-white/70 text-slate-700"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className={`${checkboxClass} accent-rose-600`}
                        checked={active}
                        disabled={!canEdit}
                        onChange={() => toggleListValue("riskIdentifiers", opt)}
                      />
                      <span>{opt}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </Section>

        <Section icon={TargetIcon} title="18. Nursing Plan of Care (POC)">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
            <Field label="Proposed Visit Frequency">
              <input
                type="text"
                className={fieldClass}
                value={values.proposedVisitFrequency}
                disabled={!canEdit}
                placeholder="e.g. 3W9"
                onChange={(e) =>
                  setValue("proposedVisitFrequency", e.target.value)
                }
              />
            </Field>
            <Field label="Estimated Discharge Date">
              <DateFieldInput
                name="estimatedDischargeDate"
                value={values.estimatedDischargeDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("estimatedDischargeDate", v)}
              />
            </Field>
            <Field label="Primary Care Coordinator">
              <input
                type="text"
                className={fieldClass}
                value={values.primaryCareCoordinator}
                disabled={!canEdit}
                placeholder="RN Case Manager"
                onChange={(e) =>
                  setValue("primaryCareCoordinator", e.target.value)
                }
              />
            </Field>
            <div className="sm:col-span-2 lg:col-span-3">
              <Field label="Clinical Goals for Episode">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.clinicalGoals}
                  disabled={!canEdit}
                  placeholder="Enter measurable goals (e.g., Patient will demonstrate proper insulin self-administration technique by visit 3...)"
                  onChange={(e) => setValue("clinicalGoals", e.target.value)}
                />
              </Field>
            </div>
          </div>
        </Section>

        <Section icon={HelpingIcon} title="19. Primary Nursing Interventions">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {NURSING_INTERVENTIONS_OPTIONS.map((opt) => {
              const active = values.nursingInterventions.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => toggleListValue("nursingInterventions", opt)}
                  className={`flex items-center justify-between gap-2 h-10 px-2.5 rounded-md text-[11px] font-semibold border text-left transition-colors disabled:opacity-60 ${
                    active
                      ? "bg-indigo-50 text-indigo-900 border-indigo-300"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <span className="truncate">{opt}</span>
                  <CheckCircleIcon
                    size={14}
                    className={
                      active ? "text-indigo-600 shrink-0" : "text-slate-300 shrink-0"
                    }
                  />
                </button>
              );
            })}
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Clinician Authentication"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

RnEvalNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  /** Present when opened from the picker before any server row exists. */
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  /** Called with the new clinical-form id after the first draft save. */
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default RnEvalNoteForm;
